import { SolButton } from "@solstice/sol-react";
import { FC } from "react";
import { ProfileProps } from "types";
import Modal from "ui-molecules/Modal/Modal";
import { ProcessingResults } from "./PublishModal";

type PublishModalResultsProps = {
  results: ProcessingResults;
  profiles: ProfileProps[];
  onClose: () => void;
  action: "publish" | "unpublish";
};

export const PublishModalResults: FC<PublishModalResultsProps> = ({
  results,
  profiles,
  onClose,
  action
}) => {
  const { successfulCount, failedCount, errors } = results;
  const totalCount = successfulCount + failedCount;

  const getProfileNameById = (id: number) => {
    const profile = profiles.find((p) => p.id === id);
    return profile
      ? `${profile.country?.name || ""} - ${profile.language_name || ""}`
      : `Profile ID: ${id}`;
  };

  const successPercentage =
    totalCount > 0 ? (successfulCount / totalCount) * 100 : 0;

  return (
    <>
      <Modal.Body>
        <div className="w-full">
          <div className="mb-6">
            <h3 className="text-xl font-medium text-gray-800 mb-4">
              Results
            </h3>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-medium">Summary</h4>
                  <p className="text-gray-500 text-sm">
                    {totalCount} profile
                    {totalCount !== 1 ? "s" : ""} processed
                  </p>
                </div>

                {failedCount === 0 ? (
                  <div className="flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full">
                    <svg
                      className="w-5 h-5 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span>All Successful</span>
                  </div>
                ) : (
                  <div className="flex items-center bg-amber-50 text-amber-700 px-3 py-1 rounded-full">
                    <svg
                      className="w-5 h-5 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span>Some Failed</span>
                  </div>
                )}
              </div>

              <div className="flex items-center mb-6">
                <div className="relative h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${successPercentage}%` }}
                  ></div>
                  {failedCount > 0 && (
                    <div
                      className="absolute top-0 right-0 h-full bg-red-500 transition-all duration-500"
                      style={{ width: `${100 - successPercentage}%` }}
                    ></div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="flex flex-col items-center p-3 bg-green-50 rounded-lg">
                  <div className="text-green-600 text-2xl font-semibold">
                    {successfulCount}
                  </div>
                  <div className="text-green-800 mt-1">Successful</div>
                </div>
                <div className="flex flex-col items-center p-3 bg-red-50 rounded-lg">
                  <div className="text-red-600 text-2xl font-semibold">
                    {failedCount}
                  </div>
                  <div className="text-red-800 mt-1">Failed</div>
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h4 className="font-medium">Error Details</h4>
                </div>
                <div className="divide-y divide-gray-200">
                  {errors.map((error) => (
                    <div key={error.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <svg
                            className="h-5 w-5 text-red-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h5 className="text-sm font-medium text-gray-900">
                            {getProfileNameById(error.id)}
                          </h5>
                          <p className="mt-1 text-sm text-gray-500">
                            {error.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {failedCount === 0 && totalCount > 0 && (
              <div className="mt-4 text-center p-6 bg-green-50 rounded-lg border border-green-200">
                <svg
                  className="mx-auto h-12 w-12 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-green-800">
                  All profiles were {action === "publish" ? "published" : "unpublished"} successfully!
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  {successfulCount} profile{successfulCount !== 1 ? "s" : ""}{" "}
                  {action === "publish" ? "published" : "unpublished"} successfully.
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end">
          <SolButton className="min-w-[96px]" onSol-click={onClose}>
            Close
          </SolButton>
        </div>
      </Modal.Footer>
    </>
  );
};